/**
 * Validate Product Form Use Case
 *
 * Business logic for validating product form data following clean architecture principles.
 * Handles form validation rules and error generation.
 *
 * @module ValidateProductFormUseCase
 */

import {ProductFormData, ProductFormValidation} from '../models/ProductFormTypes';

export class ValidateProductFormUseCase {
  /**
   * Execute form validation
   * @param formData - Form data to validate
   * @returns ProductFormValidation - Validation result
   */
  execute (formData: ProductFormData): ProductFormValidation {
    const errors: Record<string, string> = {};

    // Validate required fields
    this.validateRequiredFields(formData, errors);

    // Validate images
    this.validateImages(formData, errors);

    // Validate stock management
    this.validateStockManagement(formData, errors);

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Validate required fields
   */
  private validateRequiredFields (formData: ProductFormData, errors: Record<string, string>): void {
    if (!formData.name || !formData.name.trim()) {
      errors.name = 'اسم المنتج مطلوب';
    }

    if (!formData.type || isNaN(Number(formData.type))) {
      errors.type = 'نوع المنتج مطلوب ويجب أن يكون رقمًا';
    }

    if (!formData.brandId || isNaN(Number(formData.brandId))) {
      errors.brandId = 'البراند مطلوب ويجب أن يكون رقمًا';
    }

    if (!formData.price || isNaN(Number(formData.price))) {
      errors.price = 'السعر مطلوب ويجب أن يكون رقمًا';
    }

    if (!formData.barcode || !formData.barcode.trim()) {
      errors.barcode = 'الباركود مطلوب';
    }

    if (typeof formData.hasSerial !== 'boolean') {
      errors.hasSerial = 'يرجى تحديد إذا كان المنتج يحتوي على سيريال أم لا';
    }
  }

  /**
   * Validate images
   */
  private validateImages (formData: ProductFormData, errors: Record<string, string>): void {
    const images = Array.isArray(formData.images) ? formData.images : [];
    if (!images.length) {
      errors.images = 'الصورة مطلوبة';
    }
  }

  /**
   * Validate stock management logic
   */
  private validateStockManagement (formData: ProductFormData, errors: Record<string, string>): void {
    if (!formData.hasSerial) {
      this.validateNonSerialProduct(formData, errors);
    } else {
      this.validateSerialProduct(formData, errors);
    }
  }

  /**
   * Validate non-serial product
   */
  private validateNonSerialProduct (formData: ProductFormData, errors: Record<string, string>): void {
    if (!formData.quantity || isNaN(Number(formData.quantity)) || Number(formData.quantity) < 0) {
      errors.quantity = 'يجب تحديد الكمية الابتدائية للمنتج غير السيريال';
    }
  }

  /**
   * Validate serial product
   */
  private validateSerialProduct (formData: ProductFormData, errors: Record<string, string>): void {
    const validSerials = Array.isArray(formData.serials)
      ? formData.serials.filter(s => s && typeof s === 'string' && s.trim())
      : [];
    const expectedQuantity = validSerials.length;

    if (formData.quantity && Number(formData.quantity) !== expectedQuantity) {
      errors.quantity = `الكمية يجب أن تساوي عدد السيريالات (${expectedQuantity})`;
    }

    if (formData.serials.length > 0) {
      const serialErrors = formData.serials.filter(s => !s || typeof s !== 'string' || !s.trim());
      if (serialErrors.length > 0) {
        errors.serials = 'يجب أن تكون جميع السيريالات صحيحة ولا يمكن تركها فارغة';
      }
    }
  }

  /**
   * Validate specific field
   * @param field - Field name to validate
   * @param value - Field value
   * @param formData - Complete form data for context
   * @returns string | null - Error message or null if valid
   */
  validateField (field: keyof ProductFormData, value: unknown, formData: ProductFormData): string | null {
    const fieldValidators: Record<string, () => string | null> = {
      name: () => !value?.toString().trim() ? 'اسم المنتج مطلوب' : null,
      type: () => !value || isNaN(Number(value)) ? 'نوع المنتج مطلوب ويجب أن يكون رقمًا' : null,
      brandId: () => !value || isNaN(Number(value)) ? 'البراند مطلوب ويجب أن يكون رقمًا' : null,
      price: () => !value || isNaN(Number(value)) ? 'السعر مطلوب ويجب أن يكون رقمًا' : null,
      barcode: () => !value?.toString().trim() ? 'الباركود مطلوب' : null,
      hasSerial: () => typeof value !== 'boolean' ? 'يرجى تحديد إذا كان المنتج يحتوي على سيريال أم لا' : null,
      quantity: () => this.validateQuantityField(value, formData),
      images: () => this.validateImagesField(value),
      serials: () => this.validateSerialsField(value, formData)
    };

    const validator = fieldValidators[field];
    return validator ? validator() : null;
  }

  /**
   * Validate quantity field
   */
  private validateQuantityField (value: unknown, formData: ProductFormData): string | null {
    if (!formData.hasSerial) {
      return !value || isNaN(Number(value)) || Number(value) < 0
        ? 'يجب تحديد الكمية الابتدائية للمنتج غير السيريال'
        : null;
    }
    return null;
  }

  /**
   * Validate images field
   */
  private validateImagesField (value: unknown): string | null {
    const images = Array.isArray(value) ? value : [];
    return !images.length ? 'الصورة مطلوبة' : null;
  }

  /**
   * Validate serials field
   */
  private validateSerialsField (value: unknown, formData: ProductFormData): string | null {
    if (formData.hasSerial && Array.isArray(value) && value.length > 0) {
      const serialErrors = value.filter(s => !s || typeof s !== 'string' || !s.trim());
      return serialErrors.length > 0 ? 'يجب أن تكون جميع السيريالات صحيحة ولا يمكن تركها فارغة' : null;
    }
    return null;
  }
}
